// src/modals/RecordButton/index.tsx

import React from 'react';
import { Mic, Loader2 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import './index.scss';

const SoundWaveIcon = (props: React.SVGProps<SVGSVGElement>) => (
    <svg
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        {...props}
    >
      {/* We embed the CSS animation directly inside the SVG for portability */}
      <style>
        {`
        @keyframes soundwave-animation {
          0%, 100% { transform: scaleY(0.2); }
          25% { transform: scaleY(1); }
          50% { transform: scaleY(0.1); }
          75% { transform: scaleY(0.7); }
        }
        .sound-bar {
          transform-origin: center;
          animation: soundwave-animation 1.3s ease-in-out infinite;
        }
      `}
      </style>
      {/* The three bars of the sound wave, with staggered animation delays */}
      <path className="sound-bar" d="M6 8 L6 16" style={{ animationDelay: '0s' }} />
      <path className="sound-bar" d="M12 4 L12 20" style={{ animationDelay: '0.2s' }} />
      <path className="sound-bar" d="M18 8 L18 16" style={{ animationDelay: '0.4s' }} />
    </svg>
);

// Define the text animation for a smooth fade-and-slide effect
const textVariants = {
  initial: { opacity: 0, y: 10 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -10 },
};

/**
 * A self-contained RecordButton that displays its own status text.
 * Uses a "hold-to-record" interaction model.
 */
export default function RecordButton({
                                       onStart = () => {},
                                       onSend = () => {},
                                       onCancel = () => {},
                                       loading = false,
                                       disabled = false,
                                       status,
                                       setStatus,
                                       idleText = <>How can I help <strong>You</strong>?</>,
                                       recordingText = 'Listening...',
                                     }: {
  onStart?: () => void;
  onSend?: () => void;
  onCancel?: () => void;
  disabled?: boolean;
  loading?: boolean;
  status: 'idle' | 'recording';
  setStatus: React.Dispatch<React.SetStateAction<'idle' | 'recording'>>;
  idleText?: React.ReactNode;
  recordingText?: React.ReactNode;
}) {
  const isRecording = status === 'recording';

  const handlePointerDown = () => {
    if (disabled || loading) return;
    onStart();
    setStatus('recording');
  };

  const handlePointerUp = () => {
    if (isRecording) {
      onSend();
      setStatus('idle');
    }
  };

  const handlePointerLeave = () => {
    if (isRecording) {
      onCancel();
      setStatus('idle');
    }
  };

  return (
      <div className={`record-button-container${disabled ? ' is-disabled' : ''}`}>
        <div className="record-status-text">
          <AnimatePresence mode="wait">
            <motion.div
                key={status} // Key tells AnimatePresence to animate when the status changes
                variants={textVariants}
                initial="initial"
                animate="animate"
                exit="exit"
                transition={{ duration: 0.2 }}
            >
              {isRecording ? recordingText : ''}
            </motion.div>
          </AnimatePresence>
        </div>

        <button
            className="record-button"
            onPointerDown={handlePointerDown}
            onPointerUp={handlePointerUp}
            onPointerLeave={handlePointerLeave}
            disabled={disabled || loading}
            aria-label={isRecording ? 'Release to Send Recording' : 'Hold to Record'}
        >
          {loading ? (
              <Loader2 className="icon animate-spin" />
          ) : isRecording ? (
              <SoundWaveIcon />
          ) : (
              <Mic className="icon" />
          )}
        </button>
      </div>
  );
}